import { Components, Theme } from '@mui/material';

export const components: Components<Omit<Theme, 'components'>> = {
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 5,
        textTransform: 'none',
        fontWeight: 400,
      },
      containedPrimary: {
        backgroundColor: '#3FAE2A',
        '&:hover': {
          backgroundColor: '#2B7E1F',
        },
      },
      outlinedPrimary: {
        borderColor: '#3FAE2A',
        color: '#3FAE2A',
        '&:hover': {
          borderColor: '#2B7E1F',
          backgroundColor: 'transparent',
        },
      },
      sizeLarge: {
        height: 44,
      },
      sizeMedium: {
        height: 40,
      },
      sizeSmall: {
        height: 30,
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'medium',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 5,
          backgroundColor: '#EFF3F6',
          '&:hover': {
            backgroundColor: '#F7F9FC',
          },
          '&.Mui-focused': {
            backgroundColor: '#F7F9FC',
          },
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#B5B5B5',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: '#3FAE2A',
        },
        '& .MuiInputLabel-root': {
          color: '#576473 !important',
          fontWeight: '400 !important',
          fontSize: '14px !important',
        },
        '& .MuiInputLabel-root.Mui-focused': {
          color: '#576473 !important',
          fontWeight: '400 !important',
        },
        '& .MuiInputLabel-asterisk': {
          color: '#576473 !important',
          fontWeight: '400 !important',
        },
        '& .MuiInputBase-input': {
          color: '#141F2B !important',
          fontSize: '14px !important',
          fontWeight: '400 !important',
        },
        '& .MuiInputBase-input::placeholder': {
          color: '#576473 !important',
          fontSize: '14px !important',
          fontWeight: '400 !important',
          opacity: 1,
        },
        '& input[type="number"]': {
          fontWeight: '400 !important',
          fontSize: '14px !important',
        },
        '& input[type="date"]': {
          fontWeight: '400 !important',
          fontSize: '14px !important',
        },
        '& input[type="text"]': {
          fontWeight: '400 !important',
          fontSize: '14px !important',
        },
        '& textarea': {
          fontWeight: '400 !important',
          fontSize: '14px !important',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 6,
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
        border: '1px solid #DBE2E9',
      },
    },
  },
  MuiCardHeader: {
    styleOverrides: {
      root: {
        padding: '16px 20px',
      },
      title: {
        fontSize: '16px',
        fontWeight: 500,
      },
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        padding: '20px',
        '&:last-child': {
          paddingBottom: '20px',
        },
      },
    },
  },
  MuiCardActions: {
    styleOverrides: {
      root: {
        padding: '16px 20px',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '12px 16px',
        fontSize: '14px',
        color: '#576473',
      },
      head: {
        fontWeight: 600,
        backgroundColor: '#F7F9FC',
        color: '#141F2B',
        borderTop: '1px solid #DBE2E9',
      },
      body: {
        borderTop: '1px solid #DBE2E9',
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'rgba(247, 249, 252, 0.5)',
        },
        '&:last-child td': {
          borderBottom: 0,
        },
      },
    },
  },
  MuiTableContainer: {
    styleOverrides: {
      root: {
        overflowX: 'auto',
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        fontSize: '14px',
        height: 'auto',
        padding: '3px 5px',
      },
      colorSuccess: {
        backgroundColor: '#B8FBF0',
        color: '#2B7E1F',
      },
      colorError: {
        backgroundColor: '#FFECFC',
        color: '#9D0F41',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 6,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      rounded: {
        borderRadius: 6,
      },
      elevation1: {
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#ffffff',
        color: '#141F2B',
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: '#ffffff',
        borderRight: '1px solid #DBE2E9',
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        '& .MuiSelect-select': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
        '& .MuiInputLabel-root': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '& .MuiInputLabel-asterisk': {
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
      },
      outlined: {
        borderRadius: 5,
        fontSize: '14px !important',
        backgroundColor: '#EFF3F6',
        fontWeight: '400 !important',
      },
      select: {
        fontSize: '14px !important',
        fontWeight: '400 !important',
        color: '#141F2B !important',
      },
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        marginBottom: '16px',
        '& .MuiInputLabel-root': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '& .MuiInputLabel-asterisk': {
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '& .MuiSelect-select': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
        '& .MuiInputBase-input': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
      },
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontSize: '14px !important',
        color: '#576473 !important',
        marginBottom: '8px',
        fontWeight: '400 !important',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '14px !important',
        color: '#576473 !important',
        fontWeight: '400 !important',
        '& .MuiInputLabel-asterisk': {
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '&.Mui-focused': {
          fontSize: '14px !important',
          color: '#576473 !important',
          fontWeight: '400 !important',
        },
        '&.MuiInputLabel-shrink': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 500,
        minWidth: 'auto',
        padding: '12px 16px',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: '#3FAE2A',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        color: '#576473',
        '&:hover': {
          backgroundColor: 'rgba(247, 249, 252, 0.8)',
        },
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        backgroundColor: '#B5B5B5',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 6,
        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontSize: '18px',
        fontWeight: 600,
        padding: '16px 20px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '20px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 20px',
      },
    },
  },
  MuiAutocomplete: {
    styleOverrides: {
      root: {
        '& .MuiInputLabel-root': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '& .MuiInputLabel-asterisk': {
          fontWeight: '400 !important',
          color: '#576473 !important',
        },
        '& .MuiInputBase-input': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
        '& .MuiAutocomplete-input': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        '& .MuiFormControlLabel-label': {
          fontSize: '14px !important',
          fontWeight: '400 !important',
          color: '#141F2B !important',
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '14px !important',
        fontWeight: '400 !important',
        color: '#141F2B !important',
      },
    },
  },
};

export default components;